import React, { useState, useEffect } from 'react';
import {
    Card, CardBody, Button, Chip, Spinner, Typography,
    Dialog, DialogHeader, DialogBody, DialogFooter,
} from '@material-tailwind/react';
import {
    ArrowLeft, Download, Banknote, CheckCircle, XCircle,
    FileText, CreditCard, User, Calendar, RefreshCw, Shield, Pencil, Plus, Trash2, Save, X,
} from 'lucide-react';
import moment from 'moment';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetInvoiceById, useAddInvoicePayment, useApproveInvoicePayment, useRejectInvoicePayment, useUpdateInvoiceStatus, useSyncInvoice, useUpdateInvoiceDueDate, useUpdateInvoice, useUpdatePayment, useDeletePayment } from '../../custom-hooks/use-invoices';
import { useGetBanks } from '../../custom-hooks/use-banks';
import { INVOICE_STATUSES, INVOICE_LINE_ITEM_TYPES, PAYMENT_METHODS, BANK_TRANSFER_TYPES } from '../../utils/enums';
import { GetContractByUserId, GetInvoiceSettings } from '../../services/backend';
import { formatNumberWithCommas, Notify, convertToPresentableText } from '../../utils/functions';
import { downloadInvoicePdf } from '../../utils/invoice-pdf';
import { useConfirm } from '../../custom-hooks/confirm-dialog';

const statusColors = {
    draft: 'gray', delivered: 'blue', partially_paid: 'amber', paid: 'green', void: 'red',
};
const statusLabels = {
    draft: 'Draft', delivered: 'Delivered', partially_paid: 'Partially Paid', paid: 'Paid', void: 'Void',
};

const InvoiceDetailPage = () => {
    const { invoiceId } = useParams();
    const navigate = useNavigate();
    const { data: invoice, isLoading, refetch } = useGetInvoiceById(invoiceId);
    const paymentMutation = useAddInvoicePayment();
    const approveMutation = useApproveInvoicePayment();
    const rejectMutation = useRejectInvoicePayment();
    const statusMutation = useUpdateInvoiceStatus();
    const syncMutation = useSyncInvoice();
    const dueDateMutation = useUpdateInvoiceDueDate();
    const updateInvoiceMutation = useUpdateInvoice();
    const updatePaymentMutation = useUpdatePayment();
    const deletePaymentMutation = useDeletePayment();

    const [lineItemEditMode, setLineItemEditMode] = useState(false);
    const [editableLineItems, setEditableLineItems] = useState([]);

    const [editPaymentDialog, setEditPaymentDialog] = useState(false);
    const [editPaymentIndex, setEditPaymentIndex] = useState(null);
    const [editPaymentForm, setEditPaymentForm] = useState({
        amount: '', paymentMethod: 'cash', bankTransferType: '', note: '', paymentDate: '', bank: '',
    });

    const [paymentDialog, setPaymentDialog] = useState(false);
    const [paymentForm, setPaymentForm] = useState({
        amount: '', paymentMethod: 'cash', bankTransferType: '', note: '', paymentDate: moment().format('YYYY-MM-DD'), bank: '',
    });
    const { data: banks = [] } = useGetBanks({ activeOnly: 'true' });
    const [securityContracts, setSecurityContracts] = useState([]);
    const [securityLoading, setSecurityLoading] = useState(false);

    // Due date edit state
    const [dueDateDialog, setDueDateDialog] = useState(false);
    const [newDueDate, setNewDueDate] = useState('');

    const { confirm, ConfirmationDialog } = useConfirm();

    useEffect(() => {
        if (paymentForm.paymentMethod !== PAYMENT_METHODS.SECURITY_ADJUSTMENT || !invoice?.customer?._id) return;
        setSecurityLoading(true);
        GetContractByUserId(invoice.customer._id)
            .then(res => {
                const contracts = (res.data?.contracts || []).filter(c => !c.void?.isVoid);
                setSecurityContracts(contracts);
                const totalSecurity = contracts.reduce((sum, c) =>
                    sum + (c.bookings || []).reduce((s, b) => s + (b.securityAmount || 0), 0), 0
                );
                const applyAmount = Math.min(totalSecurity, invoice.balanceAmount || 0);
                setPaymentForm(prev => ({ ...prev, amount: String(applyAmount) }));
            })
            .catch(() => setSecurityContracts([]))
            .finally(() => setSecurityLoading(false));
    }, [paymentForm.paymentMethod, invoice?.customer?._id, invoice?.balanceAmount]);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-[50vh]">
                <Spinner />
            </div>
        );
    }

    if (!invoice) {
        return (
            <div className="p-6 text-center">
                <Typography variant="h6" color="gray">Invoice not found</Typography>
                <Button variant="text" onClick={() => navigate(-1)} className="mt-4">
                    <ArrowLeft className="h-4 w-4 mr-2" /> Go Back
                </Button>
            </div>
        );
    }

    const handleRecordPayment = async () => {
        try {
            const amt = parseFloat(paymentForm.amount);
            if (!paymentForm.amount || amt <= 0) {
                Notify('Enter a valid payment amount', 'error');
                return;
            }
            if (paymentForm.paymentMethod === PAYMENT_METHODS.BANK_TRANSFER && !paymentForm.bank) {
                Notify('Please select a bank for bank transfer', 'error');
                return;
            }
            if (paymentForm.paymentMethod === PAYMENT_METHODS.BANK_TRANSFER && !paymentForm.bankTransferType) {
                Notify('Please select Online or Cheque for bank transfer', 'error');
                return;
            }
            await paymentMutation.mutateAsync({
                invoiceId,
                data: {
                    amount: amt,
                    paymentMethod: paymentForm.paymentMethod,
                    paymentDate: paymentForm.paymentDate,
                    note: paymentForm.note,
                    bank: paymentForm.paymentMethod === PAYMENT_METHODS.BANK_TRANSFER ? paymentForm.bank : undefined,
                    bankTransferType: paymentForm.paymentMethod === PAYMENT_METHODS.BANK_TRANSFER ? paymentForm.bankTransferType : undefined,
                },
            });
            Notify('Payment recorded successfully', 'success');
            setPaymentDialog(false);
            setPaymentForm({ amount: '', paymentMethod: 'cash', bankTransferType: '', note: '', paymentDate: moment().format('YYYY-MM-DD'), bank: '' });
            setSecurityContracts([]);
            refetch();
        } catch (error) {
            Notify(error?.response?.data?.message || 'Failed to record payment', 'error');
        }
    };

    const handleUpdateDueDate = async () => {
        try {
            if (!newDueDate) {
                Notify('Please select a due date', 'error');
                return;
            }
            await dueDateMutation.mutateAsync({ invoiceId, data: { dueDate: newDueDate } });
            Notify('Due date updated successfully', 'success');
            setDueDateDialog(false);
            setNewDueDate('');
            refetch();
        } catch (error) {
            Notify(error?.response?.data?.message || 'Failed to update due date', 'error');
        }
    };

    const handleApprove = async (index) => {
        try {
            await approveMutation.mutateAsync({ invoiceId, paymentIndex: index });
            Notify('Payment approved', 'success');
            refetch();
        } catch (error) {
            Notify('Failed to approve payment', 'error');
        }
    };

    const handleReject = async (index) => {
        try {
            await rejectMutation.mutateAsync({ invoiceId, paymentIndex: index });
            Notify('Payment rejected', 'success');
            refetch();
        } catch (error) {
            Notify('Failed to reject payment', 'error');
        }
    };

    const handleStatusChange = async (newStatus) => {
        try {
            await statusMutation.mutateAsync({ invoiceId, data: { status: newStatus } });
            Notify(`Status updated to ${statusLabels[newStatus]}`, 'success');
            refetch();
        } catch (error) {
            Notify('Failed to update status', 'error');
        }
    };

    const handleSync = async () => {
        try {
            const isConfirmed = await confirm({
                title: 'Sync Invoice',
                message: 'This will re-calculate all line items based on current bookings and contracts. Any manual changes might be lost. Proceed?',
                confirmText: 'Sync Now',
                cancelText: 'Cancel',
                confirmBtnColor: 'blue',
            });
            if (isConfirmed) {
                await syncMutation.mutateAsync(invoiceId);
                Notify('Invoice synced successfully', 'success');
                refetch();
            }
        } catch (error) {
            Notify(error?.response?.data?.message || 'Sync failed', 'error');
        }
    };

    const canEditInvoice = (invoice.paidAmount || 0) === 0 &&
        [INVOICE_STATUSES.DRAFT, INVOICE_STATUSES.DELIVERED].includes(invoice.status);

    const startLineItemEdit = () => {
        setEditableLineItems((invoice.lineItems || []).map(item => ({
            type: item.type || INVOICE_LINE_ITEM_TYPES.OTHER,
            description: item.description || '',
            quantity: item.quantity || 1,
            unitPrice: item.unitPrice || 0,
            booking: item.booking?._id || item.booking,
            contract: item.contract?._id || item.contract,
        })));
        setLineItemEditMode(true);
    };

    const cancelLineItemEdit = () => {
        setLineItemEditMode(false);
        setEditableLineItems([]);
    };

    const updateEditableLineItem = (idx, field, value) => {
        setEditableLineItems(prev => prev.map((item, i) => (i === idx ? { ...item, [field]: value } : item)));
    };

    const removeEditableLineItem = (idx) => {
        setEditableLineItems(prev => prev.filter((_, i) => i !== idx));
    };

    const addEditableLineItem = () => {
        setEditableLineItems(prev => [...prev, { type: INVOICE_LINE_ITEM_TYPES.OTHER, description: '', quantity: 1, unitPrice: 0 }]);
    };

    const editableTotal = editableLineItems.reduce(
        (sum, item) => sum + (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0), 0
    );

    const handleSaveLineItems = async () => {
        try {
            if (editableLineItems.length === 0) {
                Notify('Invoice must have at least one line item', 'error');
                return;
            }
            if (editableLineItems.some(item => !item.description || !item.description.trim())) {
                Notify('Every line item needs a description', 'error');
                return;
            }
            await updateInvoiceMutation.mutateAsync({
                invoiceId,
                data: {
                    lineItems: editableLineItems.map(item => ({
                        type: item.type,
                        description: item.description,
                        quantity: Number(item.quantity) || 1,
                        unitPrice: Number(item.unitPrice) || 0,
                        booking: item.booking,
                        contract: item.contract,
                    })),
                },
            });
            Notify('Invoice updated successfully', 'success');
            setLineItemEditMode(false);
            refetch();
        } catch (error) {
            Notify(error?.response?.data?.message || 'Failed to update invoice', 'error');
        }
    };

    const canEditPayment = (p) => {
        // Day pass payments are managed from the Day Pass itself (paymentStatus there is a
        // separate field from this invoice's own status) — editing/deleting them here would
        // desync the two. Control them from the day pass, not the invoice.
        if (invoice.dayPass) return false;
        if (p.paymentMethod === PAYMENT_METHODS.SECURITY_ADJUSTMENT) return false;
        if (p.markedByCustomer && p.approvedByAdmin) return false;
        return true;
    };

    const openEditPayment = (idx) => {
        const p = invoice.payments[idx];
        setEditPaymentIndex(idx);
        setEditPaymentForm({
            amount: String(p.amount),
            paymentMethod: p.paymentMethod || PAYMENT_METHODS.CASH,
            bankTransferType: p.bankTransferType || '',
            note: p.note || '',
            paymentDate: moment(p.paymentDate).format('YYYY-MM-DD'),
            bank: p.bank?._id || p.bank || '',
        });
        setEditPaymentDialog(true);
    };

    const handleUpdatePayment = async () => {
        try {
            const amt = parseFloat(editPaymentForm.amount);
            if (!editPaymentForm.amount || amt <= 0) {
                Notify('Enter a valid payment amount', 'error');
                return;
            }
            if (editPaymentForm.paymentMethod === PAYMENT_METHODS.BANK_TRANSFER && !editPaymentForm.bank) {
                Notify('Please select a bank for bank transfer', 'error');
                return;
            }
            if (editPaymentForm.paymentMethod === PAYMENT_METHODS.BANK_TRANSFER && !editPaymentForm.bankTransferType) {
                Notify('Please select Online or Cheque for bank transfer', 'error');
                return;
            }
            await updatePaymentMutation.mutateAsync({
                invoiceId,
                paymentIndex: editPaymentIndex,
                data: {
                    amount: amt,
                    paymentMethod: editPaymentForm.paymentMethod,
                    paymentDate: editPaymentForm.paymentDate,
                    note: editPaymentForm.note,
                    bank: editPaymentForm.paymentMethod === PAYMENT_METHODS.BANK_TRANSFER ? editPaymentForm.bank : undefined,
                    bankTransferType: editPaymentForm.paymentMethod === PAYMENT_METHODS.BANK_TRANSFER ? editPaymentForm.bankTransferType : undefined,
                },
            });
            Notify('Payment updated successfully', 'success');
            setEditPaymentDialog(false);
            refetch();
        } catch (error) {
            Notify(error?.response?.data?.message || 'Failed to update payment', 'error');
        }
    };

    const handleDeletePayment = async (idx) => {
        try {
            const isConfirmed = await confirm({
                title: 'Delete Payment',
                message: 'This will permanently remove this payment. The invoice balance and status (paid / partially paid / unpaid) will be recalculated. Proceed?',
                confirmText: 'Delete Payment',
                cancelText: 'Cancel',
                confirmBtnColor: 'red',
            });
            if (!isConfirmed) return;

            await deletePaymentMutation.mutateAsync({ invoiceId, paymentIndex: idx });
            Notify('Payment deleted successfully', 'success');
            refetch();
        } catch (error) {
            Notify(error?.response?.data?.message || 'Failed to delete payment', 'error');
        }
    };

    const customer = invoice.customer || {};
    const period = invoice.billingPeriod || {};

    return (
        <div className="p-4 md:p-6 max-w-5xl mx-auto">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        <ArrowLeft className="h-5 w-5" />
                    </button>
                    <div>
                        <div className="text-2xl font-thin">{invoice.invoiceNumber}</div>
                        <div className="text-sm text-gray-500">
                            {moment({ month: period.month - 1, year: period.year }).format('MMMM YYYY')}
                        </div>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Chip
                        value={statusLabels[invoice.status] || invoice.status}
                        color={statusColors[invoice.status] || 'gray'}
                        size="md"
                        variant="ghost"
                        className="uppercase font-bold rounded-full"
                    />
                    {invoice.dayPass && (
                        <Chip
                            value="Day Pass"
                            color="cyan"
                            size="md"
                            variant="outlined"
                            className="uppercase font-bold rounded-full"
                        />
                    )}
                    {/* Sync hidden for now — the automatic per-booking sync (syncInvoiceForBooking,
                        triggered on booking create/edit/freeze/unfreeze/cancel) covers the cases this
                        was for. Left in place, not deleted, in case we want it back as an explicit
                        admin escape hatch — e.g. gated to non-manual, unpaid invoices only. */}
                    {false && !invoice.dayPass && (
                        <Button
                            variant="outlined" color="blue" size="sm"
                            className="flex items-center gap-2 rounded-full"
                            onClick={handleSync}
                            disabled={syncMutation.isLoading}
                        >
                            {syncMutation.isLoading ? <Spinner className="h-4 w-4" /> : <RefreshCw className="h-4 w-4" />}
                            Sync
                        </Button>
                    )}
                    <Button
                        variant="outlined" color="gray" size="sm"
                        className="flex items-center gap-2 rounded-full"
                        onClick={async () => {
                            try {
                                const settingsRes = await GetInvoiceSettings();
                                downloadInvoicePdf(invoice, settingsRes?.data?.settings || {});
                            } catch {
                                downloadInvoicePdf(invoice);
                            }
                        }}
                    >
                        <Download className="h-4 w-4" /> Download PDF
                    </Button>
                    {[INVOICE_STATUSES.DELIVERED, INVOICE_STATUSES.PARTIALLY_PAID].includes(invoice.status) && (
                        <Button
                            variant="gradient" color="gray" size="sm"
                            className="flex items-center gap-2 rounded-full"
                            onClick={() => setPaymentDialog(true)}
                        >
                            <Banknote className="h-4 w-4" /> Record Payment
                        </Button>
                    )}
                </div>
            </div>

            {/* Invoice Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {/* Customer Info */}
                <Card className="shadow-none border border-gray-200">
                    <CardBody className="p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <User className="h-4 w-4 text-gray-500" />
                            <Typography variant="small" className="font-semibold text-gray-600">Bill To</Typography>
                        </div>
                        <Typography className="font-medium">{customer.name || 'N/A'}</Typography>
                        <Typography variant="small" className="text-gray-500">{customer.email}</Typography>
                        {customer.phone && <Typography variant="small" className="text-gray-500">{customer.phone}</Typography>}
                    </CardBody>
                </Card>

                {/* Financial Summary */}
                <Card className="shadow-none border border-gray-200">
                    <CardBody className="p-4">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <Banknote className="h-4 w-4 text-gray-500" />
                                <Typography variant="small" className="font-semibold text-gray-600">Summary</Typography>
                            </div>
                            <span className="text-[10px] font-bold uppercase tracking-wider bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">PKR</span>
                        </div>
                        <div className="space-y-1">
                            <div className="flex justify-between">
                                <span className="text-sm text-gray-500">Subtotal</span>
                                <span className="text-sm">PKR {formatNumberWithCommas(invoice.subtotal)}</span>
                            </div>
                            <div className="flex justify-between font-medium">
                                <span className="text-sm">Total Amount</span>
                                <span className="text-sm">PKR {formatNumberWithCommas(invoice.totalAmount)}</span>
                            </div>
                            <div className="flex justify-between text-green-600">
                                <span className="text-sm">Paid</span>
                                <span className="text-sm">PKR {formatNumberWithCommas(invoice.paidAmount)}</span>
                            </div>
                            <hr className="my-1" />
                            <div className="flex justify-between font-bold text-red-600">
                                <span className="text-sm">Balance Due</span>
                                <span className="text-sm">PKR {formatNumberWithCommas(invoice.balanceAmount)}</span>
                            </div>
                        </div>
                        {invoice.dueDate && (
                            <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                                <Calendar className="h-3 w-3" />
                                <span>Due: {moment(invoice.dueDate).format('MMM DD, YYYY')}</span>
                                <button
                                    onClick={() => {
                                        setNewDueDate(moment(invoice.dueDate).format('YYYY-MM-DD'));
                                        setDueDateDialog(true);
                                    }}
                                    className="ml-1 p-0.5 hover:bg-gray-200 rounded transition-colors"
                                    title="Edit due date"
                                >
                                    <Pencil className="h-3 w-3 text-gray-400 hover:text-gray-700" />
                                </button>
                            </div>
                        )}
                        {!invoice.dueDate && (
                            <button
                                onClick={() => { setNewDueDate(''); setDueDateDialog(true); }}
                                className="mt-2 flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700"
                            >
                                <Calendar className="h-3 w-3" /> Set due date
                            </button>
                        )}
                    </CardBody>
                </Card>
            </div>

            {/* Status Actions */}
            {invoice.status !== INVOICE_STATUSES.VOID && invoice.status !== INVOICE_STATUSES.PAID && (
                <div className="flex flex-wrap gap-2 mb-6">
                    <span className="text-sm text-gray-500 flex items-center">Change Status:</span>
                    {invoice.status === INVOICE_STATUSES.DRAFT && (
                        <Button size="sm" variant="outlined" color="blue" onClick={async () => {
                            const isConfirmed = await confirm({
                                title: 'Publish Invoice',
                                message: 'Publishing this invoice will make it visible to the customer on their portal. Proceed?',
                                confirmText: 'Publish',
                                cancelText: 'Cancel',
                                confirmBtnColor: 'blue',
                            });
                            if (isConfirmed) {
                                handleStatusChange(INVOICE_STATUSES.DELIVERED);
                            }
                        }} className="rounded-full">
                            Publish to Portal
                        </Button>
                    )}
                    <Button size="sm" variant="outlined" color="red" onClick={async () => {
                        const isConfirmed = await confirm({
                            title: 'Void Invoice',
                            message: 'Voiding this invoice cannot be undone. Proceed?',
                            confirmText: 'Void Invoice',
                            cancelText: 'Cancel',
                            confirmBtnColor: 'red',
                        });
                        if (isConfirmed) {
                            handleStatusChange(INVOICE_STATUSES.VOID);
                        }
                    }} className="rounded-full">
                        Void
                    </Button>
                </div>
            )}

            {/* Line Items */}
            <Card className="shadow-none border border-gray-200 mb-6">
                <CardBody className="p-0">
                    <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-gray-500" />
                            <Typography variant="small" className="font-semibold text-gray-600">
                                Line Items ({lineItemEditMode ? editableLineItems.length : (invoice.lineItems?.length || 0)})
                            </Typography>
                        </div>
                        {!lineItemEditMode && canEditInvoice && (
                            <Button
                                variant="outlined" color="gray" size="sm"
                                className="flex items-center gap-2 rounded-full"
                                onClick={startLineItemEdit}
                            >
                                <Pencil className="h-3 w-3" /> Edit Line Items
                            </Button>
                        )}
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="border-b border-gray-200 bg-gray-50">
                                    <th className="py-3 px-4 text-xs font-semibold text-gray-600">#</th>
                                    <th className="py-3 px-4 text-xs font-semibold text-gray-600">Description</th>
                                    <th className="py-3 px-4 text-xs font-semibold text-gray-600">Type</th>
                                    <th className="py-3 px-4 text-xs font-semibold text-gray-600 text-center">Qty</th>
                                    <th className="py-3 px-4 text-xs font-semibold text-gray-600 text-right">Unit Price</th>
                                    <th className="py-3 px-4 text-xs font-semibold text-gray-600 text-right">Amount</th>
                                    {lineItemEditMode && <th className="py-3 px-4 w-10"></th>}
                                </tr>
                            </thead>
                            <tbody>
                                {!lineItemEditMode && (invoice.lineItems || []).map((item, idx) => (
                                    <tr key={idx} className="border-b border-gray-100">
                                        <td className="py-3 px-4 text-sm text-gray-500">{idx + 1}</td>
                                        <td className="py-3 px-4 text-sm">{item.description}</td>
                                        <td className="py-3 px-4 text-xs">
                                            <Chip value={convertToPresentableText(item.type)} size="sm" variant="outlined" color="gray" />
                                        </td>
                                        <td className="py-3 px-4 text-sm text-center">{item.quantity}</td>
                                        <td className="py-3 px-4 text-sm text-right">PKR {formatNumberWithCommas(item.unitPrice)}</td>
                                        <td className="py-3 px-4 text-sm text-right font-medium">PKR {formatNumberWithCommas(item.amount)}</td>
                                    </tr>
                                ))}
                                {lineItemEditMode && editableLineItems.map((item, idx) => (
                                    <tr key={idx} className="border-b border-gray-100">
                                        <td className="py-3 px-4 text-sm text-gray-500">{idx + 1}</td>
                                        <td className="py-2 px-4">
                                            <input
                                                type="text"
                                                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                                                value={item.description}
                                                onChange={(e) => updateEditableLineItem(idx, 'description', e.target.value)}
                                            />
                                        </td>
                                        <td className="py-2 px-4">
                                            <select
                                                className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs"
                                                value={item.type}
                                                onChange={(e) => updateEditableLineItem(idx, 'type', e.target.value)}
                                            >
                                                {Object.values(INVOICE_LINE_ITEM_TYPES).map(type => (
                                                    <option key={type} value={type}>{convertToPresentableText(type)}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td className="py-2 px-4">
                                            <input
                                                type="number"
                                                className="w-16 border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-center"
                                                value={item.quantity}
                                                onChange={(e) => updateEditableLineItem(idx, 'quantity', e.target.value)}
                                            />
                                        </td>
                                        <td className="py-2 px-4">
                                            <input
                                                type="number"
                                                className="w-28 border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-right"
                                                value={item.unitPrice}
                                                onChange={(e) => updateEditableLineItem(idx, 'unitPrice', e.target.value)}
                                            />
                                        </td>
                                        <td className="py-3 px-4 text-sm text-right font-medium">
                                            PKR {formatNumberWithCommas((Number(item.quantity) || 0) * (Number(item.unitPrice) || 0))}
                                        </td>
                                        <td className="py-3 px-4 text-center">
                                            <button
                                                className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                                                onClick={() => removeEditableLineItem(idx)}
                                                title="Remove line item"
                                            >
                                                <Trash2 className="h-4 w-4 text-red-500" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            {lineItemEditMode && (
                                <tfoot>
                                    <tr className="border-t border-gray-200">
                                        <td colSpan={7} className="p-3">
                                            <button
                                                className="flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-800"
                                                onClick={addEditableLineItem}
                                            >
                                                <Plus className="h-3.5 w-3.5" /> Add Line Item
                                            </button>
                                        </td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                    {lineItemEditMode && (
                        <div className="p-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
                            <div className="text-sm font-semibold">
                                Total: PKR {formatNumberWithCommas(Math.max(0, editableTotal))}
                            </div>
                            <div className="flex gap-2">
                                <Button variant="text" color="gray" size="sm" onClick={cancelLineItemEdit} className="flex items-center gap-1.5">
                                    <X className="h-3.5 w-3.5" /> Cancel
                                </Button>
                                <Button
                                    variant="gradient" color="green" size="sm"
                                    className="flex items-center gap-1.5 rounded-full"
                                    onClick={handleSaveLineItems}
                                    disabled={updateInvoiceMutation.isLoading}
                                >
                                    {updateInvoiceMutation.isLoading ? <Spinner className="h-3.5 w-3.5" /> : <Save className="h-3.5 w-3.5" />}
                                    Save Changes
                                </Button>
                            </div>
                        </div>
                    )}
                </CardBody>
            </Card>

            {/* Payment History */}
            <Card className="shadow-none border border-gray-200 mb-6">
                <CardBody className="p-0">
                    <div className="p-4 border-b border-gray-200">
                        <div className="flex items-center gap-2">
                            <CreditCard className="h-4 w-4 text-gray-500" />
                            <Typography variant="small" className="font-semibold text-gray-600">
                                Payment History ({invoice.payments?.length || 0})
                            </Typography>
                        </div>
                    </div>
                    {(!invoice.payments || invoice.payments.length === 0) ? (
                        <div className="p-8 text-center text-gray-400">
                            <Banknote className="h-8 w-8 mx-auto mb-2" />
                            <Typography variant="small">No payments recorded yet</Typography>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead>
                                    <tr className="border-b border-gray-200 bg-gray-50">
                                        <th className="py-3 px-4 text-xs font-semibold text-gray-600">Date</th>
                                        <th className="py-3 px-4 text-xs font-semibold text-gray-600 text-right">Amount</th>
                                        <th className="py-3 px-4 text-xs font-semibold text-gray-600">Method</th>
                                        <th className="py-3 px-4 text-xs font-semibold text-gray-600">Source</th>
                                        <th className="py-3 px-4 text-xs font-semibold text-gray-600">Note</th>
                                        <th className="py-3 px-4 text-xs font-semibold text-gray-600 text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoice.payments.map((p, idx) => (
                                        <tr key={idx} className={`border-b border-gray-100 ${p.paymentMethod === PAYMENT_METHODS.SECURITY_ADJUSTMENT ? 'bg-purple-50' : ''}`}>
                                            <td className="py-3 px-4 text-sm">{moment(p.paymentDate).format('MMM DD, YYYY')}</td>
                                            <td className="py-3 px-4 text-sm text-right font-medium">
                                                {p.paymentMethod === PAYMENT_METHODS.SECURITY_ADJUSTMENT ? (
                                                    <span className="text-purple-700">- PKR {formatNumberWithCommas(p.amount)}</span>
                                                ) : (
                                                    <>PKR {formatNumberWithCommas(p.amount)}</>
                                                )}
                                            </td>
                                            <td className="py-3 px-4 text-sm">
                                                {p.paymentMethod === PAYMENT_METHODS.SECURITY_ADJUSTMENT ? (
                                                    <span className="inline-flex items-center gap-1 text-purple-700 font-medium text-xs">
                                                        <Shield className="h-3 w-3" /> Security Adj.
                                                    </span>
                                                ) : (
                                                    <div>
                                                        <div>{convertToPresentableText(p.paymentMethod || '-')}</div>
                                                        {p.paymentMethod === PAYMENT_METHODS.BANK_TRANSFER && p.bankTransferType && (
                                                            <div className="text-[10px] text-blue-600 font-medium capitalize">{p.bankTransferType}</div>
                                                        )}
                                                        {p.bank?.name && (
                                                            <div className="text-[10px] text-gray-500">{p.bank.name}</div>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="py-3 px-4">
                                                {p.markedByCustomer ? (
                                                    p.approvedByAdmin ? (
                                                        <Chip value="Customer (Approved)" size="sm" color="green" variant="ghost" className="text-[10px] uppercase font-bold rounded-full text-center" />
                                                    ) : (
                                                        <Chip value="Customer (Pending)" size="sm" color="amber" variant="ghost" className="text-[10px] uppercase font-bold rounded-full text-center" />
                                                    )
                                                ) : (
                                                    <Chip
                                                        value={p.receivedBy?.name || 'Admin'}
                                                        size="sm"
                                                        color="gray"
                                                        variant="ghost"
                                                        className="text-[10px] uppercase font-bold rounded-full text-center"
                                                    />
                                                )}
                                            </td>
                                            <td className="py-3 px-4 text-sm text-gray-500">{p.note || '-'}</td>
                                            <td className="py-3 px-4 text-center">
                                                <div className="flex items-center justify-center gap-1">
                                                    {p.markedByCustomer && !p.approvedByAdmin && (
                                                        <>
                                                            <button
                                                                className="p-1.5 hover:bg-green-50 rounded-lg transition-colors"
                                                                onClick={() => handleApprove(idx)}
                                                                title="Approve"
                                                                disabled={approveMutation.isLoading}
                                                            >
                                                                <CheckCircle className="h-4 w-4 text-green-600" />
                                                            </button>
                                                            <button
                                                                className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                                                                onClick={() => handleReject(idx)}
                                                                title="Reject"
                                                                disabled={rejectMutation.isLoading}
                                                            >
                                                                <XCircle className="h-4 w-4 text-red-600" />
                                                            </button>
                                                        </>
                                                    )}
                                                    {canEditPayment(p) && (
                                                        <>
                                                            <button
                                                                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                                                                onClick={() => openEditPayment(idx)}
                                                                title="Edit payment"
                                                            >
                                                                <Pencil className="h-4 w-4 text-gray-500" />
                                                            </button>
                                                            <button
                                                                className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                                                                onClick={() => handleDeletePayment(idx)}
                                                                title="Delete payment"
                                                                disabled={deletePaymentMutation.isLoading}
                                                            >
                                                                <Trash2 className="h-4 w-4 text-red-500" />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardBody>
            </Card>

            {/* Notes */}
            {invoice.notes && (
                <Card className="shadow-none border border-gray-200 mb-6">
                    <CardBody className="p-4">
                        <Typography variant="small" className="font-semibold text-gray-600 mb-2">Notes</Typography>
                        <Typography variant="small" className="text-gray-500">{invoice.notes}</Typography>
                    </CardBody>
                </Card>
            )}

            {/* Record Payment Dialog */}
            <Dialog open={paymentDialog} handler={() => { setPaymentDialog(false); setSecurityContracts([]); }} size="sm">
                <DialogHeader>Record Payment</DialogHeader>
                <DialogBody className="space-y-4">
                    <div>
                        <label className="text-sm text-gray-600 mb-1 block">Payment Method</label>
                        <select
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            value={paymentForm.paymentMethod}
                            onChange={(e) => {
                                setSecurityContracts([]);
                                setPaymentForm(prev => ({ ...prev, paymentMethod: e.target.value, amount: '', bank: '', bankTransferType: '' }));
                            }}
                        >
                            <option value={PAYMENT_METHODS.CASH}>Cash</option>
                            <option value={PAYMENT_METHODS.BANK_TRANSFER}>Bank Transfer</option>
                            <option value={PAYMENT_METHODS.SECURITY_ADJUSTMENT}>Security Adjustment</option>
                        </select>
                    </div>

                    {/* Bank Transfer sub-options: Online or Cheque */}
                    {paymentForm.paymentMethod === PAYMENT_METHODS.BANK_TRANSFER && (
                        <div>
                            <label className="text-sm text-gray-600 mb-1 block">Transfer Type *</label>
                            <select
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                value={paymentForm.bankTransferType}
                                onChange={(e) => setPaymentForm(prev => ({ ...prev, bankTransferType: e.target.value }))}
                            >
                                <option value="">Select type</option>
                                <option value={BANK_TRANSFER_TYPES.ONLINE}>Online</option>
                                <option value={BANK_TRANSFER_TYPES.CHEQUE}>Cheque</option>
                            </select>
                        </div>
                    )}

                    {/* Bank selector — only when method is bank_transfer */}
                    {paymentForm.paymentMethod === PAYMENT_METHODS.BANK_TRANSFER && (
                        <div>
                            <label className="text-sm text-gray-600 mb-1 block">Bank *</label>
                            <select
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                value={paymentForm.bank}
                                onChange={(e) => setPaymentForm(prev => ({ ...prev, bank: e.target.value }))}
                            >
                                <option value="">Select bank</option>
                                {banks.map(b => (
                                    <option key={b._id} value={b._id}>
                                        {b.name}{b.accountTitle ? ` — ${b.accountTitle}` : ''}
                                    </option>
                                ))}
                            </select>
                            {banks.length === 0 && (
                                <p className="text-[10px] text-amber-600 mt-1">
                                    No banks configured. Add one in Settings → Payments.
                                </p>
                            )}
                        </div>
                    )}

                    {/* Security Adjustment: show breakdown from active contracts */}
                    {paymentForm.paymentMethod === PAYMENT_METHODS.SECURITY_ADJUSTMENT && (
                        <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 space-y-2">
                            <div className="flex items-center gap-2 text-blue-700">
                                <Shield className="h-4 w-4 flex-shrink-0" />
                                <span className="text-xs font-semibold uppercase tracking-wide">Security from Active Contracts</span>
                            </div>
                            {securityLoading ? (
                                <div className="flex items-center gap-2 text-xs text-blue-600"><Spinner className="h-3 w-3" /> Loading...</div>
                            ) : securityContracts.length === 0 ||
                              securityContracts.reduce((sum, c) =>
                                sum + (c.bookings || []).reduce((s, b) => s + (b.securityAmount || 0), 0), 0) === 0 ? (
                                <p className="text-xs text-red-500 font-medium">No security available — cannot record this payment.</p>
                            ) : (
                                <div className="space-y-1">
                                    {securityContracts.map((c, i) => {
                                        const sec = (c.bookings || []).reduce((s, b) => s + (b.securityAmount || 0), 0);
                                        return sec > 0 ? (
                                            <div key={i} className="flex justify-between text-xs text-gray-700">
                                                <span>Contract #{i + 1} ({moment(c.startDate).format('MMM YYYY')})</span>
                                                <span className="font-medium">PKR {formatNumberWithCommas(sec)}</span>
                                            </div>
                                        ) : null;
                                    })}
                                    <div className="flex justify-between text-xs font-bold text-blue-800 border-t border-blue-200 pt-1 mt-1">
                                        <span>Total Security</span>
                                        <span>PKR {formatNumberWithCommas(
                                            securityContracts.reduce((sum, c) =>
                                                sum + (c.bookings || []).reduce((s, b) => s + (b.securityAmount || 0), 0), 0)
                                        )}</span>
                                    </div>
                                </div>
                            )}
                            <p className="text-[10px] text-gray-400 mt-1">Only the applied amount is deducted from security. Any remaining security stays on the contracts.</p>
                        </div>
                    )}

                    <div>
                        <label className="text-sm text-gray-600 mb-1 block">Amount (PKR) *</label>
                        <input
                            type="number"
                            className={`w-full border rounded-lg px-3 py-2 text-sm ${
                                paymentForm.paymentMethod === PAYMENT_METHODS.SECURITY_ADJUSTMENT
                                    ? 'border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed'
                                    : paymentForm.amount && parseFloat(paymentForm.amount) > invoice.balanceAmount
                                        ? 'border-red-500 focus:outline-none focus:ring-2 focus:ring-red-400'
                                        : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500'
                            }`}
                            placeholder={`Balance: ${formatNumberWithCommas(invoice.balanceAmount)}`}
                            value={paymentForm.amount}
                            readOnly={paymentForm.paymentMethod === PAYMENT_METHODS.SECURITY_ADJUSTMENT}
                            onChange={(e) => {
                                if (paymentForm.paymentMethod !== PAYMENT_METHODS.SECURITY_ADJUSTMENT) {
                                    setPaymentForm(prev => ({ ...prev, amount: e.target.value }));
                                }
                            }}
                        />
                        {paymentForm.paymentMethod === PAYMENT_METHODS.SECURITY_ADJUSTMENT ? (
                            <p className="text-[10px] text-gray-400 mt-1">Amount is capped at the invoice balance. Remaining security stays on the contracts.</p>
                        ) : paymentForm.amount && parseFloat(paymentForm.amount) > invoice.balanceAmount ? (
                            <p className="text-xs text-red-500 mt-1">Amount cannot exceed the balance due (PKR {formatNumberWithCommas(invoice.balanceAmount)})</p>
                        ) : null}
                    </div>
                    <div>
                        <label className="text-sm text-gray-600 mb-1 block">Payment Date</label>
                        <input
                            type="date"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            value={paymentForm.paymentDate}
                            onChange={(e) => setPaymentForm(prev => ({ ...prev, paymentDate: e.target.value }))}
                        />
                    </div>
                    <div>
                        <label className="text-sm text-gray-600 mb-1 block">Note</label>
                        <textarea
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            rows={2}
                            placeholder="Optional note..."
                            value={paymentForm.note}
                            onChange={(e) => setPaymentForm(prev => ({ ...prev, note: e.target.value }))}
                        />
                    </div>
                </DialogBody>
                <DialogFooter>
                    <Button variant="text" onClick={() => { setPaymentDialog(false); setSecurityContracts([]); }} className="mr-2">Cancel</Button>
                    <Button
                        variant="gradient" color="green"
                        onClick={handleRecordPayment}
                        disabled={
                            paymentMutation.isLoading ||
                            (paymentForm.amount && parseFloat(paymentForm.amount) > invoice.balanceAmount) ||
                            (paymentForm.paymentMethod === PAYMENT_METHODS.BANK_TRANSFER && !paymentForm.bank) ||
                            (paymentForm.paymentMethod === PAYMENT_METHODS.BANK_TRANSFER && !paymentForm.bankTransferType) ||
                            (paymentForm.paymentMethod === PAYMENT_METHODS.SECURITY_ADJUSTMENT &&
                                (securityLoading || securityContracts.reduce((sum, c) =>
                                    sum + (c.bookings || []).reduce((s, b) => s + (b.securityAmount || 0), 0), 0) === 0))
                        }
                        className="flex items-center gap-2"
                    >
                        {paymentMutation.isLoading ? <Spinner className="h-4 w-4" /> : <Banknote className="h-4 w-4" />}
                        Record Payment
                    </Button>
                </DialogFooter>
            </Dialog>

            {/* Due Date Edit Dialog */}
            <Dialog open={dueDateDialog} handler={() => setDueDateDialog(false)} size="xs">
                <DialogHeader>Edit Due Date</DialogHeader>
                <DialogBody>
                    <div>
                        <label className="text-sm text-gray-600 mb-1 block">New Due Date</label>
                        <input
                            type="date"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            value={newDueDate}
                            onChange={(e) => setNewDueDate(e.target.value)}
                        />
                    </div>
                </DialogBody>
                <DialogFooter>
                    <Button variant="text" onClick={() => setDueDateDialog(false)} className="mr-2">Cancel</Button>
                    <Button
                        variant="gradient" color="blue"
                        onClick={handleUpdateDueDate}
                        disabled={dueDateMutation.isLoading || !newDueDate}
                        className="flex items-center gap-2"
                    >
                        {dueDateMutation.isLoading ? <Spinner className="h-4 w-4" /> : <Calendar className="h-4 w-4" />}
                        Save
                    </Button>
                </DialogFooter>
            </Dialog>

            {/* Edit Payment Dialog */}
            <Dialog open={editPaymentDialog} handler={() => setEditPaymentDialog(false)} size="sm">
                <DialogHeader>Edit Payment</DialogHeader>
                <DialogBody className="space-y-4">
                    <div>
                        <label className="text-sm text-gray-600 mb-1 block">Payment Method</label>
                        <select
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            value={editPaymentForm.paymentMethod}
                            onChange={(e) => setEditPaymentForm(prev => ({ ...prev, paymentMethod: e.target.value, bank: '', bankTransferType: '' }))}
                        >
                            <option value={PAYMENT_METHODS.CASH}>Cash</option>
                            <option value={PAYMENT_METHODS.BANK_TRANSFER}>Bank Transfer</option>
                        </select>
                    </div>

                    {editPaymentForm.paymentMethod === PAYMENT_METHODS.BANK_TRANSFER && (
                        <div>
                            <label className="text-sm text-gray-600 mb-1 block">Transfer Type *</label>
                            <select
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                value={editPaymentForm.bankTransferType}
                                onChange={(e) => setEditPaymentForm(prev => ({ ...prev, bankTransferType: e.target.value }))}
                            >
                                <option value="">Select type</option>
                                <option value={BANK_TRANSFER_TYPES.ONLINE}>Online</option>
                                <option value={BANK_TRANSFER_TYPES.CHEQUE}>Cheque</option>
                            </select>
                        </div>
                    )}

                    {editPaymentForm.paymentMethod === PAYMENT_METHODS.BANK_TRANSFER && (
                        <div>
                            <label className="text-sm text-gray-600 mb-1 block">Bank *</label>
                            <select
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                value={editPaymentForm.bank}
                                onChange={(e) => setEditPaymentForm(prev => ({ ...prev, bank: e.target.value }))}
                            >
                                <option value="">Select bank</option>
                                {banks.map(b => (
                                    <option key={b._id} value={b._id}>
                                        {b.name}{b.accountTitle ? ` — ${b.accountTitle}` : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div>
                        <label className="text-sm text-gray-600 mb-1 block">Amount (PKR) *</label>
                        <input
                            type="number"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            value={editPaymentForm.amount}
                            onChange={(e) => setEditPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                        />
                    </div>
                    <div>
                        <label className="text-sm text-gray-600 mb-1 block">Payment Date</label>
                        <input
                            type="date"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            value={editPaymentForm.paymentDate}
                            onChange={(e) => setEditPaymentForm(prev => ({ ...prev, paymentDate: e.target.value }))}
                        />
                    </div>
                    <div>
                        <label className="text-sm text-gray-600 mb-1 block">Note</label>
                        <textarea
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            rows={2}
                            value={editPaymentForm.note}
                            onChange={(e) => setEditPaymentForm(prev => ({ ...prev, note: e.target.value }))}
                        />
                    </div>
                </DialogBody>
                <DialogFooter>
                    <Button variant="text" onClick={() => setEditPaymentDialog(false)} className="mr-2">Cancel</Button>
                    <Button
                        variant="gradient" color="green"
                        onClick={handleUpdatePayment}
                        disabled={updatePaymentMutation.isLoading}
                        className="flex items-center gap-2"
                    >
                        {updatePaymentMutation.isLoading ? <Spinner className="h-4 w-4" /> : <Save className="h-4 w-4" />}
                        Save Changes
                    </Button>
                </DialogFooter>
            </Dialog>

            <ConfirmationDialog />
        </div>
    );
};

export default InvoiceDetailPage;
